import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useEffect, useState} from "react";

import {Id, View} from "@khanacademy/wonder-blocks-core";
import {OptionItem, SingleSelect} from "@khanacademy/wonder-blocks-dropdown";
import Link from "@khanacademy/wonder-blocks-link";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {color, sizing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    LabelLarge,
    LabelMedium,
} from "@khanacademy/wonder-blocks-typography";
import {useMutation} from "@repo/data-gql";

import {SET_PRIMARY_EMAIL_MUTATION} from "./set-primary-email-mutation.ts";

const strings = {
    connectEmailHere: (connect: React.ReactNode) => (
        <Trans>To use an email address not listed here, {connect}.</Trans>
    ),
} as const;

interface Props {
    email: string;
    authEmails: Array<string | null | undefined>;
}

export const PrimaryEmailSection: React.FC<Props> = ({email, authEmails}) => {
    const [selectedEmail, setSelectedEmail] = useState(email);
    const [saving, setSaving] = useState(false);
    const [updateEmail] = useMutation(SET_PRIMARY_EMAIL_MUTATION);

    // Track component mounted state to prevent state updates after unmount
    const [isMounted, setIsMounted] = useState(true);
    useEffect(() => {
        return () => {
            setIsMounted(false);
        };
    }, []);

    const handleChange = async (selected: string) => {
        if (!isMounted) {
            return;
        }

        const oldEmail = selectedEmail;
        setSelectedEmail(selected);
        setSaving(true);

        try {
            const result = await updateEmail({
                email: selected,
            });

            if (isMounted) {
                if (
                    result.status === "success" &&
                    result.data?.setPrimaryEmail?.error
                ) {
                    setSelectedEmail(oldEmail);
                } else {
                    setSelectedEmail(selected);
                }
            }
        } catch (error) {
            if (isMounted) {
                setSelectedEmail(oldEmail);
            }
        } finally {
            if (isMounted) {
                setSaving(false);
            }
        }
    };

    const renderEmailSelect = (emailSelectId: string) => {
        return (
            <SingleSelect
                onChange={handleChange}
                placeholder=""
                selectedValue={selectedEmail}
                id={emailSelectId}
                disabled={saving}
            >
                {authEmails.map(
                    (email) =>
                        !!email && (
                            <OptionItem
                                label={email}
                                value={email}
                                key={email}
                            />
                        ),
                )}
            </SingleSelect>
        );
    };

    const renderSection = (idRoot: string) => {
        const emailSelectId = `${idRoot}email`;

        // NOTE: This doesn't actually scroll to the linked accounts section,
        // as switching tabs kicks off a new query (see componentDidMount in
        // settings-page.jsx). Ideally we'd use something like
        // react-router-hash-link.
        const connect = (
            <Link href="/settings/account#linked-accounts">
                {t`connect a new email to your account`}
            </Link>
        );

        return (
            <View>
                <View style={styles.row}>
                    <LabelMedium
                        htmlFor={emailSelectId}
                        style={[styles.label, styles.verticalCenter]}
                        tag="label"
                    >
                        {t`Send email to`}
                    </LabelMedium>
                    <View style={{width: sizing.size_160}} />
                    {authEmails.length > 1 ? (
                        renderEmailSelect(emailSelectId)
                    ) : (
                        <LabelLarge id={emailSelectId}>
                            {selectedEmail}
                        </LabelLarge>
                    )}
                    {saving && (
                        <View style={{marginInlineStart: sizing.size_160}}>
                            <CircularSpinner size="small" testId="spinner" />
                        </View>
                    )}
                </View>
                <View style={{marginBlockStart: sizing.size_160}}>
                    <Body>{strings.connectEmailHere(connect)}</Body>
                </View>
            </View>
        );
    };

    return <Id>{(idRoot) => renderSection(idRoot)}</Id>;
};

const styles = StyleSheet.create({
    label: {
        color: color.offBlack,
    },
    row: {
        flexDirection: "row",
        alignItems: "center",
        gap: sizing.size_160, // Replaced Strut with gap
    },
    verticalCenter: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
    },
});

export default PrimaryEmailSection;
